import { Box, List, ListItem, SxProps, Theme, Typography } from '@mui/material'
import moment from 'moment'
import React, { useMemo } from 'react'
import { WarningIcon } from 'src/Libs/flow/icons'
import { AuthenticationInfo } from 'src/Libs/flow/pages/AuthenticationInfo'
import { ChargingSessionModel } from 'src/Libs/flow/pages/charging-session-models'
import { getSessionStatusTranslation } from 'src/Libs/flow/pages/charging-session-parser'
import { formatDuration } from 'src/Libs/flow/pages/utils/format-duration'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { localizeEmobilityNumber } from 'src/Libs/flow/pages/utils/localize-emobility-number'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { PricingInfo } from './PricingInfo'

const listItemSx: SxProps<Theme> = {
  alignItems: 'center',
  py: 1.5,
  pl: 0,
}
const listItemTitleSx: SxProps<Theme> = {
  overflowWrap: 'anywhere',
  hyphens: 'auto',
  width: '33%',
}
const listItemValueSx: SxProps<Theme> = {
  width: '67%',
  pl: 1,
  overflowWrap: 'break-word',
}

export interface ChargingSessionDetailsDialogV1FlowProps {
  open: boolean
  onClose: () => void
  place: number
  installationId: number
  session: ChargingSessionModel
}

export const ChargingSessionDetailsV1FlowDialog: React.FC<ChargingSessionDetailsDialogV1FlowProps> = ({ installationId, session, open, onClose }) => {
  const { t } = useCustomTranslation()

  const chargingGrid = useMemo<number>(() => {
    const totalGridCharging = session.energyAll - session.energySolar

    if (totalGridCharging < 0) return 0
    return totalGridCharging / 1000
  }, [session])

  const sessionMidAnomalyError =
    session.midAnomaly === 'InconsistentData'
      ? t('emobility.chargingSession.mid-anomaly-error')
      : session.midAnomaly === 'Offline'
      ? t('emobility.chargingSession.mid-anomaly-offline-error')
      : ''

  return (
    <Dialog
      title={t('emobility.chargingSession.chargingSession', '', { sessionID: session.sessionID })}
      onClose={onClose}
      maxWidth={'sm'}
      actions={{
        secondary: {
          name: t('common.close'),
        },
      }}
    >
      <List>
        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.wallbox')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {session.deviceName}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.status')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {getSessionStatusTranslation(t, session.status)}
            {!!session.midAnomaly && (
              <Box>
                <WarningIcon color="warning" sx={{ float: 'left' }} />
                {t('emobility.chargingSession.charge-measurement-anomaly')}
              </Box>
            )}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.timerange')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {`${moment.utc(session.startAt).local().format(t('common.format.dateTime'))} - ${
              session.stopAt ? moment.utc(session.stopAt).local().format(t('common.format.dateTime')) : 'N/A'
            }`}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.created')}
          </Typography>

          <Typography variant="body2" sx={listItemValueSx}>
            {moment.utc(session.startAt).local().format(t('common.format.dateTime'))}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.updated')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {moment.utc(session.updatedAt).local().format(t('common.format.dateTime'))}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.authentication')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            <AuthenticationInfo rfid={session.authentication.data ?? null} cardName={session.authentication.cardName} />
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.authenticationType.name')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {session.authentication.type === 'NONE'
              ? t('emobility.chargingSession.AuthenticationType.none')
              : session.authentication.type === 'RFID'
              ? t('emobility.chargingSession.AuthenticationType.rfid')
              : t('emobility.chargingSession.AuthenticationType.cpoRfid')}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.loaded')}
          </Typography>

          <Typography variant="body2" sx={listItemValueSx}>
            {`${t('emobility.chargingSession.chargingGrid')} ${t('common.format.kwh', '', {
              value: localizeEmobilityNumber(chargingGrid),
            })} • ${t('emobility.chargingSession.chargingSolar')} ${t('common.format.kwh', '', {
              value: localizeEmobilityNumber(session.energySolar / 1000),
            })} • ${t('emobility.chargingSession.chargingAll')} ${t('common.format.kwh', '', {
              value: localizeEmobilityNumber(session.energyAll / 1000),
            })}`}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.mid-certified-label')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {session.isMidCertified ? t('emobility.chargingSession.mid-certified') : t('emobility.chargingSession.not-mid-certified')}
          </Typography>
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.mid-anomaly-label')}
          </Typography>
          {session.midAnomaly ? (
            <Typography variant="body2" sx={listItemValueSx}>
              {sessionMidAnomalyError}
            </Typography>
          ) : (
            <Typography variant="body2" sx={listItemValueSx} color={'textDisabled'}>
              {t('emobility.chargingSession.mid-anomaly-no-issue')}
            </Typography>
          )}
        </ListItem>

        <ListItem divider sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.chargeDuration')}
          </Typography>
          <Typography variant="body2" sx={listItemValueSx}>
            {`${t('emobility.chargingSession.active')} ${formatDuration(session.chargeTimeActive, t)} • ${t(
              'emobility.chargingSession.inactive'
            )} ${formatDuration(session.chargeTimeInactive, t)}`}
          </Typography>
        </ListItem>

        <ListItem sx={listItemSx} disableGutters>
          <Typography variant="body2" sx={listItemTitleSx}>
            {t('emobility.chargingSession.price')}
          </Typography>
          <PricingInfo session={session} sx={listItemValueSx} installationId={installationId} />
        </ListItem>
      </List>
    </Dialog>
  )
}
